// «A quién le aplica» de venta y referidos: columnas de segmento y de crédito SK, como Alcance.
// El resumen lo pinta `SalePromotionSegmentationSummary` junto al título de la sección.
import { useFormContext, useWatch } from 'react-hook-form';
import { AudienceSummary } from '@/shared/patterns/Lifecycle';
import { ALL_SEGMENTS_LABEL, ScopeChipGrid, ScopeChoiceGroup } from '@/shared/patterns/ScopeChips';
import {
    ANY_CUSTOMER_LABEL,
    SK_CREDIT_REQUIRED,
    SK_CREDIT_REQUIRED_LABEL,
} from '../constants/audience';
import { SALE_PROMOTION_HELP } from '../constants/helpText';
import type { SalePromotionSegment } from '../api/promotionsContract';
import type { PromotionsAvailability } from '../lib/promotionsFormat';
import { describeSegmentation } from '../lib/promotionsList';
import type { SalePromotionFormValues } from '../lib/salePromotionForm';
import { segmentsNotice } from '../lib/segmentAvailability';

interface SegmentChoice {
    segmentId: string;
    name: string;
}

/** Los segmentos del tenant en orden alfabético, más los guardados que ya no llegan del catálogo. */
function segmentChoices(segments: readonly SalePromotionSegment[], selected: string[]) {
    const known = new Set(segments.map((segment) => segment.segmentId));
    const choices: SegmentChoice[] = [
        ...segments.map((segment) => ({
            segmentId: segment.segmentId,
            name: segment.name,
        })),
        ...selected.filter((id) => !known.has(id)).map((id) => ({ segmentId: id, name: id })),
    ];
    return choices.sort((a, b) => a.name.localeCompare(b.name, 'es-MX'));
}

/** La segmentación elegida en una línea, para el resumen de la sección. */
export function SalePromotionSegmentationSummary({
    segments,
}: {
    segments: readonly SalePromotionSegment[];
}) {
    const { control } = useFormContext<SalePromotionFormValues>();
    const [segmentIds, requiresSk] = useWatch({
        control,
        name: ['segmentIds', 'requiresActiveSurekeepCredit'],
    });
    const names = new Map(segmentChoices(segments, segmentIds).map((c) => [c.segmentId, c.name]));
    return (
        <AudienceSummary>
            {describeSegmentation(
                { segmentation: { segmentIds, requiresActiveSurekeepCredit: requiresSk } },
                names,
            )}
        </AudienceSummary>
    );
}

export function SalePromotionSegmentationEditor({
    segments,
    segmentsState,
}: {
    segments: readonly SalePromotionSegment[];
    segmentsState: PromotionsAvailability;
}) {
    const { control, setValue } = useFormContext<SalePromotionFormValues>();
    const [segmentIds, requiresSk] = useWatch({
        control,
        name: ['segmentIds', 'requiresActiveSurekeepCredit'],
    });
    const choices = segmentChoices(segments, segmentIds);
    const notice = segmentsNotice(segmentsState);

    const setSegments = (next: string[]) => {
        setValue('segmentIds', next, { shouldDirty: true });
    };
    const setRequiresSk = (next: boolean) => {
        if (next !== requiresSk)
            setValue('requiresActiveSurekeepCredit', next, { shouldDirty: true });
    };

    return (
        <ScopeChipGrid>
            <ScopeChoiceGroup
                dim="segment"
                title="Segmento"
                help={SALE_PROMOTION_HELP.segments}
                allLabel={ALL_SEGMENTS_LABEL}
                allSelected={segmentIds.length === 0}
                onSelectAll={() => {
                    if (segmentIds.length > 0) setSegments([]);
                }}
                options={choices.map((choice) => ({
                    value: choice.segmentId,
                    label: choice.name,
                    selected: segmentIds.includes(choice.segmentId),
                }))}
                onToggle={(segmentId) => {
                    setSegments(
                        segmentIds.includes(segmentId)
                            ? segmentIds.filter((id) => id !== segmentId)
                            : [...segmentIds, segmentId],
                    );
                }}
                emptyLabel={notice ?? 'El tenant no tiene segmentos.'}
                notice={
                    choices.length > 0 && notice ? (
                        <p className="mb-1.5 text-[10.5px] text-muted-foreground">{notice}</p>
                    ) : null
                }
            />
            <ScopeChoiceGroup
                dim="tier"
                title="Crédito SK"
                help={SALE_PROMOTION_HELP.surekeepCredit}
                allLabel={ANY_CUSTOMER_LABEL}
                selection="single"
                allSelected={!requiresSk}
                onSelectAll={() => {
                    setRequiresSk(false);
                }}
                options={[
                    {
                        value: SK_CREDIT_REQUIRED,
                        label: SK_CREDIT_REQUIRED_LABEL,
                        selected: requiresSk,
                    },
                ]}
                onToggle={() => {
                    setRequiresSk(true);
                }}
            />
        </ScopeChipGrid>
    );
}
